import {
  IconCheck,
  IconClock,
  IconKeyboard,
  IconLanguage,
  IconListNumbers,
  IconPercentage,
  IconSum,
} from '@tabler/icons-react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import LanguageModel from '@Api/models/LanguageModel';
import { TypingMode } from '@Api/models/TypingTestResultModel';
import TypingTestResultRankingTestsTakenListModel from '@Api/models/TypingTestResultRankingTestsTakenListModel';
import TypingTestResultRankingWpmModel from '@Api/models/TypingTestResultRankingWpmModel';

import Col from '@Components/atoms/Col';
import { Icon } from '@Components/atoms/Icon/Icon';
import Row from '@Components/atoms/Row';
import { RowGap } from '@Components/atoms/Row/Row';
import Skeleton from '@Components/atoms/Skeleton';
import Text from '@Components/atoms/Text';
import { Tooltip, TooltipContent, TooltipTrigger } from '@Components/atoms/Tooltip/Tooltip';
import { PropsWithClassName } from '@Components/helper';
import { capitalizeFirstLetter } from '@Components/helper/language';
import { getStringWithLength } from '@Components/helper/utils';
import TypingTestHighscoreTestsTakenRank from '@Components/molecules/TypingTestHighscoreTestsTakenRank/TypingTestHighscoreTestsTakenRank';
import TypingTestHighscoreWpmRank from '@Components/molecules/TypingTestHighscoreWpmRank/TypingTestHighscoreWpmRank';

import { TextAlign, TextColor, TextSize } from '@Helpers/types/text';

import {
  CreatedAtIconWrapper,
  EmptyRow,
  PercentageIconWrapper,
  PositionHeaderText,
  ScoreIconWrapper,
  StyledDivider,
  StyledHeadline,
  TestsTakenIconWrapper,
  WpmHeaderText,
} from './TypingTestHighscore.styles';

interface Props extends PropsWithClassName {
  rankingWpm?: TypingTestResultRankingWpmModel[];
  rankingTestsTaken?: TypingTestResultRankingTestsTakenListModel;
  isLoadingWpm: boolean;
  isLoadingTestsTaken: boolean;
  language: LanguageModel;
  typingMode: TypingMode;
}

const TypingTestHighscore = (props: Props): React.ReactElement => {
  const {
    rankingWpm,
    rankingTestsTaken,
    isLoadingWpm,
    isLoadingTestsTaken,
    language,
    typingMode,
    dataTestId = 'typing-test-highscore',
  } = props;

  const { t } = useTranslation('typing-test');

  const renderEmptyHighscore = useCallback(
    () => (
      <EmptyRow xsCenter>
        <Text size={TextSize.Large} textAlign={TextAlign.Center}>
          {t('highscore.empty')}
        </Text>
      </EmptyRow>
    ),
    [t]
  );

  const getTestsTakenInterval = useCallback(() => {
    switch (rankingTestsTaken?.meta.interval) {
      case '-24 hour':
        return t('highscore.sub_headline.24_hours');
      case '-3 days':
        return t('highscore.sub_headline.3_days');
      case '-7 days':
        return t('highscore.sub_headline.7_days');
    }
  }, [rankingTestsTaken, t]);

  const renderTestsTakenRankRow = useCallback(() => {
    if (isLoadingTestsTaken) {
      return [...Array(23)].map((value, index) => <TypingTestHighscoreTestsTakenRank.Skeleton key={index} />);
    }

    if (!rankingTestsTaken || rankingTestsTaken.data.length === 0) {
      return renderEmptyHighscore();
    }

    return rankingTestsTaken.data.map((result, index) => (
      <div key={index}>
        {index !== 0 && <StyledDivider />}
        <TypingTestHighscoreTestsTakenRank result={result} position={index + 1} interval={getTestsTakenInterval()} />
      </div>
    ));
  }, [getTestsTakenInterval, isLoadingTestsTaken, rankingTestsTaken, renderEmptyHighscore]);

  const renderWpmRankRow = useCallback(() => {
    if (isLoadingWpm) {
      return [...Array(23)].map((value, index) => <TypingTestHighscoreWpmRank.Skeleton key={index} />);
    }

    if (!rankingWpm || rankingWpm.length === 0) {
      return renderEmptyHighscore();
    }

    return rankingWpm.map((result, index) => (
      <div key={index}>
        {index !== 0 && <StyledDivider />}
        <TypingTestHighscoreWpmRank resultModel={result} position={index + 1} />
      </div>
    ));
  }, [isLoadingWpm, rankingWpm, renderEmptyHighscore]);

  return (
    <Row
      dataTestId={dataTestId + '-root'}
      className={props.className}
      xsBetween
      rowGap={RowGap.Default}
      columnGap={RowGap.Xl}
    >
      <Col xs={12} sm={5} md={5} lg={5} dataTestId={dataTestId + '-wpm'}>
        <Row noGutters xsCenter>
          <StyledHeadline importance={4}>{t('highscore.headline.ranking_wpm')}</StyledHeadline>
        </Row>
        <Row xsCenter rowGap={RowGap.Default} noGutters>
          <Row noGutters rowGap={RowGap.Xs}>
            <Icon icon={IconLanguage} size={18} />
            <Text size={TextSize.Xs}> {capitalizeFirstLetter(language.name)}</Text>
          </Row>
          <Row rowGap={RowGap.Xs} noGutters>
            <Icon icon={IconKeyboard} size={18} />
            <Text size={TextSize.Xs}> {t(`typing_test_mode.${typingMode}`, { ns: 'global' })}</Text>
          </Row>
        </Row>
        <Row noGutters xsBetween xsBottom>
          <PositionHeaderText size={TextSize.Xs} bold color={TextColor.Tertiary} textAlign={TextAlign.Center}>
            #
          </PositionHeaderText>
          <Row noGutters rowGap={RowGap.Xs} xsBottom>
            <Tooltip>
              <TooltipTrigger>
                <ScoreIconWrapper>
                  <Icon icon={IconListNumbers} size={16} strokeWidth={1.5} />
                </ScoreIconWrapper>
              </TooltipTrigger>
              <TooltipContent isSmall>
                <Text size={TextSize.Small}>{t('highscore.header.score')}</Text>
              </TooltipContent>
            </Tooltip>
            <WpmHeaderText size={TextSize.Xs} textAlign={TextAlign.Right} bold>
              {t('highscore.header.wpm')}
            </WpmHeaderText>
            <Tooltip>
              <TooltipTrigger>
                <CreatedAtIconWrapper>
                  <Icon icon={IconClock} size={16} strokeWidth={1.5} />
                </CreatedAtIconWrapper>
              </TooltipTrigger>
              <TooltipContent isSmall>
                <Text size={TextSize.Small}>{t('highscore.header.created_at')}</Text>
              </TooltipContent>
            </Tooltip>
          </Row>
        </Row>
        {renderWpmRankRow()}
      </Col>
      <Col xs={12} sm={5} md={5} lg={5} dataTestId={dataTestId + '-tests-taken'}>
        <Row noGutters xsCenter>
          <StyledHeadline importance={4}> {t('highscore.headline.tests_taken_wpm')}</StyledHeadline>
        </Row>
        <Row xsCenter rowGap={RowGap.Default} noGutters>
          <Row noGutters rowGap={RowGap.Xs}>
            <Icon icon={IconSum} size={18} />
            {isLoadingTestsTaken || rankingTestsTaken === undefined ? (
              <Skeleton>
                <Text size={TextSize.Xs}>{getStringWithLength(4)}</Text>
              </Skeleton>
            ) : (
              <Text size={TextSize.Xs}>{rankingTestsTaken.data[0]?.totalTestsTaken ?? 0}</Text>
            )}
          </Row>
          <Row rowGap={RowGap.Xs} noGutters>
            <Icon icon={IconClock} size={18} />
            <Text size={TextSize.Xs}> {getTestsTakenInterval()}</Text>
          </Row>
        </Row>
        <Row noGutters xsBetween xsBottom>
          <PositionHeaderText size={TextSize.Xs} bold color={TextColor.Tertiary} textAlign={TextAlign.Center}>
            #
          </PositionHeaderText>
          <Row noGutters rowGap={RowGap.Xs} xsBottom>
            <Tooltip>
              <TooltipTrigger>
                <PercentageIconWrapper>
                  <Icon icon={IconPercentage} size={16} strokeWidth={1.5} />
                </PercentageIconWrapper>
              </TooltipTrigger>
              <TooltipContent isSmall>
                <Text size={TextSize.Small}>{t('highscore.header.total_tests_taken_percentage')}</Text>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger>
                <TestsTakenIconWrapper>
                  <Icon icon={IconCheck} size={16} strokeWidth={1.5} />
                </TestsTakenIconWrapper>
              </TooltipTrigger>
              <TooltipContent isSmall>
                <Text size={TextSize.Small}>{t('highscore.header.tests')}</Text>
              </TooltipContent>
            </Tooltip>
          </Row>
        </Row>
        {renderTestsTakenRankRow()}
      </Col>
    </Row>
  );
};
export default TypingTestHighscore;
